import React from 'react';
import Reveal from './Reveal';

const centsToDisplay = (cents) => {
  if (!Number.isFinite(cents)) return null;
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
};

const formatWindowDate = (value, includeTime = false) => {
  if (!value) return null;
  return new Date(value).toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
    hour: includeTime ? 'numeric' : undefined,
    minute: includeTime ? '2-digit' : undefined,
    timeZoneName: includeTime ? 'short' : undefined,
  });
};

const TIER_ART = {
  super_early_bird: {
    src: '/images/courses/pricing-super-early-bird.webp',
    alt: 'Super Early Bird',
  },
  early_bird: {
    src: '/images/courses/pricing-early-bird.webp',
    alt: 'Early Bird',
  },
  full_price: {
    src: '/images/courses/pricing-full-price.webp',
    alt: 'Full price',
  },
};

/**
 * One clear when-line per window so the ladder reads as:
 * now / until date → next date → later date.
 */
const windowWhenLine = (window, previousWindow) => {
  const ends = formatWindowDate(window.endDate);
  const starts = formatWindowDate(window.startDate);

  if (window.isCurrent) {
    return ends ? `Until ${ends}` : 'Price right now';
  }
  if (window.tier === 'full_price') {
    if (starts && previousWindow?.endDate && formatWindowDate(previousWindow.endDate) === starts) {
      return `After ${previousWindow.label} ends on ${formatWindowDate(previousWindow.endDate, true)}`;
    }
    return starts ? `From ${starts}` : 'After early bird ends';
  }
  if (starts && ends) return `${starts} to ${ends}`;
  if (starts) return `From ${starts}`;
  return null;
};

const installmentLine = (plan) => {
  const price = centsToDisplay(plan.amountCents);
  if (!price || plan.installments <= 1) return null;
  return `${plan.installments}× ${price}/mo`;
};

/**
 * Transparent pricing windows from /checkout/catalog tuitionSchedule.
 * Prices and dates come from the live catalog. Layout leads with the price
 * and the date it changes, not a wall of plan copy.
 */
const TuitionSchedule = ({ schedule, cohortName = null, showArt = true }) => {
  if (!schedule?.windows?.length) return null;

  const liveCohortName = cohortName || schedule.cohortName || null;

  return (
    <section className="cp-section cp-tuition" data-testid="tuition-schedule">
      <Reveal>
        <h2>How pricing works</h2>
      </Reveal>
      <Reveal delay={40}>
        <p className="cp-tuition-lede" data-testid="tuition-lede">
          {liveCohortName
            ? `${liveCohortName}: the price goes up on set dates.`
            : 'The price goes up on set dates.'}{' '}
          Enroll earlier and pay less.
        </p>
      </Reveal>
      <div className="cp-tuition-grid">
        {schedule.windows.map((window, index) => {
          const upfront = window.plans.find((plan) => plan.installments === 1) || window.plans[0];
          const installments = window.plans.filter((plan) => plan.installments > 1);
          const art = TIER_ART[window.tier] || null;
          const price = centsToDisplay(upfront?.amountCents);
          const list = centsToDisplay(upfront?.listAmountCents);
          const when = windowWhenLine(window, schedule.windows[index - 1]);
          const showWas = Boolean(list && price && list !== price);

          return (
            <Reveal
              key={`${window.tier}-${window.endDate || window.startDate || index}`}
              delay={60 + index * 40}
              className={`cp-tuition-card${window.isCurrent ? ' is-current' : ''}`.trim()}
            >
              {showArt && art ? (
                <div className="cp-tuition-art">
                  <img src={art.src} alt={art.alt} width={640} height={640} loading="lazy" />
                </div>
              ) : null}
              <div className="cp-tuition-card-head">
                <h3>{window.label}</h3>
                {window.isCurrent ? <span className="cp-tuition-badge">Now</span> : null}
              </div>
              {price ? (
                <p className="cp-tuition-price" data-testid={`tuition-${window.tier}-upfront`}>
                  <span className="cp-tuition-price-now">{price}</span>
                  {showWas ? <s className="cp-tuition-was">was {list}</s> : null}
                </p>
              ) : null}
              {when ? (
                <p className="cp-tuition-dates" data-testid={`tuition-${window.tier}-when`}>
                  <span className="cp-tuition-date-line">{when}</span>
                </p>
              ) : null}
              {installments.length ? (
                <ul
                  className="cp-tuition-installments"
                  aria-label={`${window.label} payment plans`}
                >
                  {installments.map((plan) => {
                    const line = installmentLine(plan);
                    return line ? <li key={plan.planKey}>{line}</li> : null;
                  })}
                </ul>
              ) : null}
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export default TuitionSchedule;
